import React from 'react';
import { useLocation } from '@reach/router';
import queryString from 'query-string';
import Button from '$/components/ui/Button';
import { InRoomButton } from '$/components/ui/Button/InRoomButton';
import { createBackendUrl } from '$/utils/urls';
import { PublicSetFragment, SsoUserInfo, useSsoUserInfoLazyQuery } from '$/GraphQL/schema/schema';

interface SSOAuthHook {
  authorized: boolean;
  requireSSO: boolean;
  login: (parentUrl?: string) => void;
  loading: boolean;
  userInfo?: SsoUserInfo;
}

export const useSSOAuth = (set?: PublicSetFragment, attendeeId?: string): SSOAuthHook => {
  const [userInfo, setUserInfo] = React.useState<SsoUserInfo>();
  const [userInfoQuery, userInfoResult] = useSsoUserInfoLazyQuery();

  const requireSSO = Boolean(set?.setSettings?.accessControl?.requireSSO);
  const [authorized, setAuthorized] = React.useState(!requireSSO);

  React.useEffect(() => {
    if (!requireSSO || !set?.id) {
      return;
    }

    userInfoQuery({
      variables: {
        setId: set.id,
        attendeeId: attendeeId,
      },
    });
  }, [userInfoQuery, attendeeId, set?.id, requireSSO]);

  React.useEffect(() => {
    if (!requireSSO) {
      return;
    }

    if (userInfoResult?.data?.ssoUserInfo) {
      setAuthorized(true);
      setUserInfo(userInfoResult?.data?.ssoUserInfo);
    } else {
      setAuthorized(false);
    }
  }, [requireSSO, userInfoResult?.data?.ssoUserInfo]);

  const login = React.useCallback(
    (parentUrl?: string): void => {
      if (!set?.id) {
        return;
      }
      const searchParams: Record<string, string> = {
        setId: set.id,
        redirectSuccessUrl: location.pathname,
      };
      if (attendeeId) {
        searchParams['attendeeId'] = attendeeId;
      }
      if (parentUrl) {
        searchParams.redirectSuccessUrl = parentUrl;
        // this flag is used to auto open Registration Form Widget
        // @todo: probably we need Widget ID instead to identify the widget if there are multiple widgets
        searchParams.ewidget = '1';
      }
      const url = createBackendUrl('v1/integrations/sso', searchParams);
      if (parentUrl) {
        parent.location.href = url;
      } else {
        document.location.href = url;
      }
    },
    [attendeeId, set?.id]
  );

  return {
    authorized,
    userInfo,
    login,
    requireSSO,
    loading: Boolean(userInfoResult?.loading),
  };
};

interface SSOLoginProps {
  set: PublicSetFragment;
  attendeeId?: string;
  className?: string;
  parentUrl?: string; // parent window of Widget
  children?: React.ReactNode;
}

export const SSOLoginButton: React.FC<SSOLoginProps> = ({ set, children, parentUrl, ...props }) => {
  const { authorized, login, loading } = useSSOAuth(set);
  if (authorized) {
    return null;
  }
  return (
    <Button.submit
      onClick={(e): void => {
        e.preventDefault();
        login(parentUrl);
      }}
      {...props}
      submitting={loading}
    >
      {children || 'Login to register'}
    </Button.submit>
  );
};

const AuthErrorMessage: React.FC = () => {
  const location = useLocation();
  if (!location.search) {
    return null;
  }
  const query = queryString.parse(location.search);
  if (!query.terror) {
    return null;
  }
  const error = query.terror as string;
  return <div className='flex flex-col text-2xl text-center items-center mb-2e'>{error}</div>;
};

export const SSOLoginButtonInRoom: React.FC<SSOLoginProps> = ({
  set,
  attendeeId,
  children,
  ...props
}) => {
  const { authorized, login, loading } = useSSOAuth(set, attendeeId);
  if (authorized) {
    return null;
  }
  return (
    <>
      <AuthErrorMessage />
      <InRoomButton onClick={(): void => login()} {...props} submitting={loading}>
        {children || 'Login to Join'}
      </InRoomButton>
    </>
  );
};
